/**
 * Text on the whiteboard is SVG `<text>`, wrapped here — not HTML inside a `<foreignObject>`, which WebKit (the iOS app,
 * Safari) draws in the wrong place under a scaled group. So the wrapping is ours: measured with a canvas in the same
 * font the SVG uses, breaking at spaces, and between any two CJK characters (Chinese has no spaces to break at).
 */
export const BOARD_FONT = "ui-sans-serif, system-ui, -apple-system, 'Noto Sans TC', sans-serif"
export const LINE_HEIGHT = 1.3

let ctx: CanvasRenderingContext2D | null | undefined
function measurer(): CanvasRenderingContext2D | null {
  if (ctx === undefined) {
    try {
      ctx = typeof document === 'undefined' ? null : document.createElement('canvas').getContext('2d')
    } catch {
      ctx = null
    }
  }
  return ctx
}

const widthCache = new Map<string, number>()

/** The width of a run of text; without a canvas (tests), a fair estimate: CJK a full em, the rest ~0.55 em. */
export function textWidth(s: string, fs: number, bold = false): number {
  const key = `${bold ? 'b' : 'n'}${fs}|${s}`
  const hit = widthCache.get(key)
  if (hit !== undefined) return hit
  const c = measurer()
  let w: number
  if (c) {
    c.font = `${bold ? '600 ' : ''}${fs}px ${BOARD_FONT}`
    w = c.measureText(s).width
  } else {
    w = 0
    for (const ch of s) w += CJK.test(ch) ? fs : fs * 0.55
  }
  if (widthCache.size > 5000) widthCache.clear()
  widthCache.set(key, w)
  return w
}

/** A note's monospace (its `code`), beside BOARD_FONT. */
export const MONO_FONT = "ui-monospace, SFMono-Regular, Menlo, Consolas, monospace"

/** The width of a run in a style of its own — a note's Markdown (noteText.ts): weight, italics, monospace. */
export function styledWidth(s: string, fs: number, style: { bold?: boolean; italic?: boolean; mono?: boolean }): number {
  if (!style.italic && !style.mono) return textWidth(s, fs, !!style.bold)
  const key = `${style.bold ? 'b' : 'n'}${style.italic ? 'i' : ''}${style.mono ? 'm' : ''}${fs}|${s}`
  const hit = widthCache.get(key)
  if (hit !== undefined) return hit
  const c = measurer()
  let w: number
  if (c) {
    c.font = `${style.italic ? 'italic ' : ''}${style.bold ? '600 ' : ''}${fs}px ${style.mono ? MONO_FONT : BOARD_FONT}`
    w = c.measureText(s).width
  } else {
    w = 0
    for (const ch of s) w += CJK.test(ch) ? fs : fs * (style.mono ? 0.6 : 0.55)
  }
  if (widthCache.size > 5000) widthCache.clear()
  widthCache.set(key, w)
  return w
}

export const CJK = /[⺀-鿿가-힯豈-﫿＀-￯　-〿]/
export const TOKENS = /[⺀-鿿가-힯豈-﫿＀-￯　-〿]|[^\s⺀-鿿가-힯豈-﫿＀-￯　-〿]+|\s+/g

/** The lines `text` takes in a box `maxWidth` wide. Hard line breaks are kept; an over-long word is cut. */
export function wrap(text: string, fs: number, maxWidth: number, bold = false): string[] {
  const out: string[] = []
  const max = Math.max(fs, maxWidth)
  for (const para of text.split('\n')) {
    const toks = para.match(TOKENS) ?? []
    let line = ''
    const push = () => {
      out.push(line.replace(/\s+$/, ''))
      line = ''
    }
    for (const tok of toks) {
      if (textWidth(line + tok, fs, bold) <= max) {
        line += tok
        continue
      }
      if (/^\s+$/.test(tok)) {
        push()
        continue
      }
      if (line.trim()) push()
      else line = ''
      if (textWidth(tok, fs, bold) <= max) {
        line = tok
        continue
      }
      // A word wider than the box: cut it where it reaches the edge.
      for (const ch of tok) {
        if (line && textWidth(line + ch, fs, bold) > max) push()
        line += ch
      }
    }
    push()
  }
  return out
}

/** How tall `lines` stand at `fs`. */
export function blockHeight(lines: number, fs: number): number {
  return lines * fs * LINE_HEIGHT
}
